/* ----------------- React --------------- */
import { useState } from "react";

import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { AppReviewDialog } from "@features/feedback/components/dialogs/AppReviewDialog";
import { SessionConfigDialog } from "@features/session-config/components/SessionConfigDialog";
/* ----------------- Hooks --------------- */
import BottomFab from "@memorang/ui/components/BottomFab";
/* ----------------- UI --------------- */
import Box from "@memorang/ui/components/Box";
import { CustomAppBar } from "@memorang/ui/components/CustomAppbar";
import { useHome } from "../hooks/useHome";
import { HomeView } from "./HomeView";

/* ----------------- Helpers --------------- */
import { isWeb } from "@helpers/platform";

/* ----------------- Router --------------- */
import { useLocalSearchParams, useNavigation, useRouter } from "expo-router";

import { cacheKeys } from "@constants/cache-keys";
import { events } from "@constants/tracking";
import CollectUserDetailsDialog from "@features/account/components/dialogs/CollectUserDetailsDialog";
import FreeQuizSessionDialog from "@features/auth/components/dialogs/FreeQuizSessionDialog";
import useMultipleExams from "@features/exam/contexts/useMultipleExams";
import { getShowStudyPackViews } from "@helpers/exams";
import { useAppTheme } from "@hooks/useAppTheme";
import { useCustomNavigation } from "@hooks/useCustomNavigation";
import type { ExamDetails } from "@memorang/types";
import { TextSkeleton } from "@memorang/ui/components/ChatUI/TextSkeleton";
import Container from "@memorang/ui/components/Container";
import { useDevice } from "@memorang/ui/useDevice";
import { useQueryClient } from "@tanstack/react-query";
/* ----------------- Analytics --------------- */
import { trackCustomEvent } from "analytics";
import { ExamsFilterDialog } from "components/ExamsFilterDialog";
import SetExamDateContainer from "components/SetExamDateContainer";
import { Pressable } from "react-native";
import { Text } from "react-native-paper";
import { OLXExamEligibilityDialog } from "../../../components/OLXExamEligibilityDialog";
import AppContextBannerContainer from "../containers/AppContextBannerContainer";
import { NestedExamChipsContainer } from "../containers/NestedExamChipsContainer";
import { PurchaseDialog } from "./PurchaseDialog";

export const HomeRouteContainer = () => {
	const {
		currentExamContext: { studyPackCollectionId, examName },
	} = useExamContext();
	const theme = useAppTheme();

	const [showExamsFilterDialog, setShowExamsFilterDialog] = useState(false);

	const toggleShowExamsFilterDialog = () => {
		setShowExamsFilterDialog((prev) => !prev);
	};
	const { isMobile, isDesktopWeb } = useDevice();
	const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);

	const toggleShowPurchaseDialog = () => {
		setShowPurchaseDialog((prev) => !prev);
	};

	const { startDiagnostic } = useLocalSearchParams<{
		startDiagnostic: string;
	}>();

	const { relatedExams } = useMultipleExams();

	const router = useRouter();
	const navigation = useNavigation<{ toggleDrawer?: () => void }>();

	const queryClient = useQueryClient();
	const cachedExams = queryClient.getQueryData<ExamDetails[]>([
		cacheKeys.exams,
	]);

	const hasMultipleExams = cachedExams?.length && cachedExams?.length > 1;

	const showStudyPackViews = getShowStudyPackViews(studyPackCollectionId);

	const [selectedWeakTags, setSelectedWeakTags] = useState<
		string[] | undefined
	>(undefined);

	const {
		sections,
		headerData,
		showPurchaseDialog: showPracticeTestPurchaseDialog,
		toggleShowPurchaseDialog: toggleShowPracticeTestPurchaseDialog,
		olxError,
		setOlxError,
		showCollectUserDetailsDialog,
		toggleShowCollectUserDetailsDialog,
		showDatePicker,
		toggleShowDatePicker,
		hasAccess,
		exams,
	} = useHome();

	const { handleExamsFilterNavigation } = useCustomNavigation();

	const handleSessionConfigNavigation = (tagIds: number[]) => {
		if (isDesktopWeb) {
			setSelectedWeakTags(tagIds.map((id) => id.toString()));
		} else {
			router.push({
				pathname: "/session/customize",
				params: {
					selectedTagIds: tagIds,
				},
			});
		}
	};

	const handleQuickStudy = () => {
		trackCustomEvent({
			eventName: events.studyFabClicked,
		});
		router.push("/session/customize");
	};

	const showSessionConfigDialog = selectedWeakTags != null;

	const showBottomFab = isMobile && showStudyPackViews;

	const handleSelectTopic = (topicId: number) => {
		handleSessionConfigNavigation([topicId]);
	};

	// const redeemableProducts = testRedeemableProducts;

	const showFreeQuizDialog = startDiagnostic === "true";

	const handleShowPurchase = () => {
		if (isWeb) {
			toggleShowPurchaseDialog?.();
		} else {
			router.push({
				pathname: "/purchase",
				params: {
					productType: studyPackCollectionId ? "STUDY_PACK" : "PRACTICE_TEST",
				},
			});
		}
	};

	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: no need to extract this function
	const ExamChooserTitle = () => {
		return examName ? (
			<Pressable
				// @ts-ignore web specific property
				style={{
					cursor: hasMultipleExams ? "pointer" : "default",
				}}
				onPress={
					hasMultipleExams
						? () => handleExamsFilterNavigation(toggleShowExamsFilterDialog)
						: undefined
				}
			>
				<Box
					gap={6}
					flexDirection="row"
					alignItems="center"
					paddingRight={isWeb ? 16 : 0}
				>
					<Text
						numberOfLines={2}
						variant={isMobile ? "titleLarge" : "titleSmall"}
						style={{ flex: isMobile ? 1 : undefined }}
					>
						{examName}
					</Text>
					{hasMultipleExams ? (
						<Icon
							name="unfold-more-horizontal"
							size={20}
							color={theme.colors.onSurfaceVariant}
						/>
					) : null}
				</Box>
			</Pressable>
		) : (
			<Box maxWidth={400}>
				<TextSkeleton height={26} />
			</Box>
		);
	};
	return (
		<Box position="relative" flexGrow={1} height={"100%"}>
			<CustomAppBar
				title={<ExamChooserTitle />}
				navigation={navigation}
				handleTrackEvent={() => {
					trackCustomEvent({
						eventName: events.commandBarClicked,
						option: "command_bar",
					});
				}}
			/>
			<Box flex={1}>
				<AppContextBannerContainer />
				{relatedExams.length > 0 && (
					<Container
						maxWidth="sm"
						autoSize
						containerStyle={{
							paddingTop: isMobile ? 16 : 24,
							paddingHorizontal: isMobile ? 0 : 8,
							paddingBottom: isMobile ? 16 : 0,
						}}
					>
						<NestedExamChipsContainer />
					</Container>
				)}
				<HomeView
					headerData={headerData}
					sections={sections}
					handleShowPurchase={handleShowPurchase}
					handleSelectTopic={handleSelectTopic}
					hasAccess={hasAccess}
					exams={exams}
					showPurchaseDialog={showPracticeTestPurchaseDialog}
					toggleShowPurchaseDialog={toggleShowPracticeTestPurchaseDialog}
				/>
			</Box>
			{showBottomFab && (
				<BottomFab
					icon={"play-outline"}
					label="Quick Study"
					onPress={handleQuickStudy}
					bottom={16}
				/>
			)}

			{showSessionConfigDialog ? (
				<SessionConfigDialog
					visible={showSessionConfigDialog}
					selectedTagIds={selectedWeakTags?.map((id) => Number(id))}
					handleClose={() => setSelectedWeakTags(undefined)}
				/>
			) : null}
			{!isWeb && <AppReviewDialog />}

			{showFreeQuizDialog && (
				<FreeQuizSessionDialog
					visible={showFreeQuizDialog}
					onClose={() => {
						router.setParams({
							startDiagnostic: "false",
						});
					}}
				/>
			)}
			{showPurchaseDialog && (
				<PurchaseDialog
					visible={showPurchaseDialog}
					handleClose={toggleShowPurchaseDialog}
					productType="STUDY_PACK"
				/>
			)}
			{!!olxError && (
				<OLXExamEligibilityDialog
					visible={!!olxError}
					handleClose={() => setOlxError?.(null)}
					title={olxError?.title}
					message={olxError?.message}
				/>
			)}
			{showCollectUserDetailsDialog && toggleShowCollectUserDetailsDialog && (
				<CollectUserDetailsDialog
					visible={showCollectUserDetailsDialog}
					handleClose={toggleShowCollectUserDetailsDialog}
				/>
			)}
			<SetExamDateContainer
				showDatePicker={showDatePicker}
				toggleShowDatePicker={toggleShowDatePicker}
			/>
			{showExamsFilterDialog && (
				<ExamsFilterDialog
					visible={showExamsFilterDialog}
					handleClose={toggleShowExamsFilterDialog}
				/>
			)}
		</Box>
	);
};
